import { useTheme } from '../../hooks/useTheme'
import type { ReactNode } from 'react'

interface QuickActionButtonProps {
  icon: string
  title: string
  description: string
  onClick: () => void
  badge?: ReactNode
  className?: string
}

/**
 * Reusable quick action button component for dashboard actions
 * 
 * @example
 * <QuickActionButton
 *   icon="👥"
 *   title="View All Trainees"
 *   description="Manage your trainees"
 *   onClick={() => navigate('/trainees')}
 * />
 */
export function QuickActionButton({
  icon,
  title,
  description,
  onClick,
  badge,
  className = '',
}: QuickActionButtonProps) {
  const theme = useTheme()

  return (
    <button 
      className={`w-full rounded-xl p-4 mb-3 flex items-center gap-3 cursor-pointer transition-all hover:opacity-80 hover:translate-x-1 active:translate-x-0.5 active:scale-[0.98] text-left ${className}`}
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.cardBorder}`,
      }}
      onClick={onClick}
    >
      <span className="text-2xl flex-shrink-0">{icon}</span>
      <div className="flex-1">
        <div className="text-base font-semibold mb-1 flex items-center gap-2">
          {title}
          {badge}
        </div>
        <div className="text-sm opacity-70">{description}</div>
      </div>
      <span className="text-xl opacity-50 flex-shrink-0">→</span>
    </button>
  )
}
